import React, { useState } from "react";
import PropTypes from "prop-types";
import ClickAwayListener from "@mui/material/ClickAwayListener";
import Box from "@mui/material/Box";
import Drawer from "@mui/material/Drawer";
import Paper from "@mui/material/Paper";
import List from "@mui/material/List";
import ListItem from "@mui/material/ListItem";
import Typography from "@mui/material/Typography";
import useMediaQuery from "@mui/material/useMediaQuery";
import SkuflixVoiceIcon from "core/icons/skuflix/SkuflixVoiceIcon";
import { useTranslation } from "react-i18next";
import PlayerFooterButton from "core/components/PlayerFooterButton";

function AudioTrackButton({ videoActions, videoState }) {
  const { t } = useTranslation("core");
  const isMobile = useMediaQuery("(max-width: 450px)");

  const [open, setOpen] = useState(false);

  const handleClick = () => {
    setOpen((v) => !v);
  };

  const handleClickAway = () => {
    setOpen(false);
  };

  const handleClickItem = (track) => () => {
    videoActions.selectAudioTrack(track);
    setOpen(false);
  };

  if (!videoState) return null;

  const audioTracks = videoState.audioTracks || [];

  if (audioTracks.length < 2) return null;

  const button = (
    <PlayerFooterButton
      title={open ? "" : t("audioTrackButton.title")}
      onClick={handleClick}
    >
      <SkuflixVoiceIcon />
    </PlayerFooterButton>
  );

  const listItems = audioTracks.map((track, index) => {
    const languageLabel =
      track.language && track.language !== "und" ? track.language : "default";

    const titleLabel = track.label || "Default";
    const label = `${titleLabel} (${languageLabel})`;

    return (
      <ListItem
        key={`${track.language}_${track.label}_${index}`}
        button
        selected={videoState.selectedAudioTrackKey === track.key}
        onClick={handleClickItem(track)}
      >
        {label}
      </ListItem>
    );
  });

  if (isMobile) {
    return (
      <>
        <Box sx={{ position: "relative" }}>{button}</Box>
        <Drawer
          anchor="bottom"
          open={open}
          onClose={handleClickAway}
          disableScrollLock
          PaperProps={{
            sx: {
              borderTopLeftRadius: 12,
              borderTopRightRadius: 12,
              maxHeight: "60vh",
            },
          }}
        >
          <Typography sx={{ px: 2, pt: 2, pb: 1, fontWeight: 500 }}>
            {t("audioTrackButton.title")}
          </Typography>
          <List disablePadding dense>
            {listItems}
          </List>
        </Drawer>
      </>
    );
  }

  return (
    <ClickAwayListener onClickAway={handleClickAway}>
      <Box
        sx={{
          position: "relative",
          zIndex: (theme) => (open ? theme.zIndex.tooltip : "auto"),
        }}
      >
        {button}
        {open && (
          <Paper
            sx={{
              position: "absolute",
              bottom: 60,
              right: -12,
              overflow: "hidden",
            }}
          >
            <List disablePadding dense>
              {listItems}
            </List>
          </Paper>
        )}
      </Box>
    </ClickAwayListener>
  );
}

AudioTrackButton.propTypes = {
  videoState: PropTypes.object,
  videoActions: PropTypes.object,
};

export default AudioTrackButton;
